import React, { useState, useCallback, memo, useEffect } from 'react';
import Icon from './AppIcon';
import Button from './ui/Button';
import Input from './ui/Input';
import useLanguage from '../hooks/useLanguage';
import { useToast } from './ui/Toast';

const PartialCloseModal = memo(({ isOpen, onClose, position, onPartialClose }) => {
  const [volumeToClose, setVolumeToClose] = useState('');
  const [percentage, setPercentage] = useState(50);
  const { t, isRTL } = useLanguage();
  const toast = useToast();

  useEffect(() => {
    if (position) {
      // Default to 50% of position
      const halfVolume = ((position.amount || position.volume) / 2).toFixed(2);
      setVolumeToClose(halfVolume);
      setPercentage(50);
    }
  }, [position]);

  const handlePercentageChange = useCallback((value) => {
    setPercentage(value);
    if (position) {
      const totalVolume = position.amount || position.volume;
      const calculatedVolume = ((totalVolume * value) / 100).toFixed(2);
      setVolumeToClose(calculatedVolume);
    }
  }, [position]);

  const handleVolumeChange = useCallback((value) => {
    setVolumeToClose(value);
    if (position) {
      const totalVolume = position.amount || position.volume;
      const calculatedPercentage = Math.round((parseFloat(value) / totalVolume) * 100);
      setPercentage(Math.min(100, Math.max(0, calculatedPercentage)));
    }
  }, [position]);

  const handlePartialClose = useCallback(async () => {
    const volumeNum = parseFloat(volumeToClose);
    const totalVolume = position.amount || position.volume;

    if (!volumeNum || volumeNum <= 0) {
      toast.error(t('invalidVolume'), 3000);
      return;
    }

    if (volumeNum > totalVolume) {
      toast.error(t('volumeTooHigh'), 3000);
      return;
    }

    try {
      // Calculate profit for closed portion
      const closedProfit = (position.profit * volumeNum) / totalVolume;
      
      await onPartialClose(position.id, volumeNum, closedProfit);
      
      toast.success(
        `${position.symbol} ${t('partialClose')}\n${volumeNum} ${t('lot')} ${t('closed')} - ${t('profit')}: ${closedProfit >= 0 ? '+' : ''}$${closedProfit.toFixed(2)}`,
        5000
      );
      
      onClose();
    } catch (error) {
      console.error('Failed to partially close position:', error);
      toast.error(t('partialCloseFailed'), 4000);
    }
  }, [position, volumeToClose, onPartialClose, onClose, toast, t]);

  if (!isOpen || !position) return null;

  const totalVolume = position.amount || position.volume;
  const remainingVolume = (totalVolume - parseFloat(volumeToClose || 0)).toFixed(2);
  const isBuy = position.type === 'buy' || position.type?.includes('buy');

  return (
    <div className={`fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 ${isRTL ? 'rtl' : ''}`}>
      <div className="bg-card rounded-lg shadow-lg w-full max-w-md">
        {/* Modal Header */}
        <div className="flex items-center justify-between p-4 border-b border-border">
          <h2 className="text-lg font-semibold text-foreground">
            {t('partialClose')} - {position.symbol}
          </h2>
          <button
            onClick={onClose}
            className="p-1 hover:bg-muted rounded"
          >
            <Icon name="X" size={20} className="text-muted-foreground" />
          </button>
        </div>

        {/* Modal Content */}
        <div className="p-4 space-y-4">
          {/* Position Info */}
          <div className="bg-muted p-3 rounded space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-muted-foreground">{t('symbol')}:</span>
              <span className="font-medium">{position.symbol}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">{t('type')}:</span>
              <span className={`font-medium ${isBuy ? 'text-green-600' : 'text-red-600'}`}>
                {position.type?.toUpperCase()}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">{t('totalVolume')}:</span>
              <span className="font-medium">{totalVolume} {t('lot')}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">{t('currentPrice')}:</span>
              <span className="font-medium font-mono">{position.currentPrice?.toFixed(5)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">{t('totalProfit')}:</span>
              <span className={`font-medium ${position.profit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                {position.profit >= 0 ? '+' : ''}${position.profit?.toFixed(2)}
              </span>
            </div>
          </div>

          {/* Percentage Slider */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-foreground">
              {t('closePercentage')}: {percentage}%
            </label>
            <input
              type="range"
              min="1"
              max="100"
              value={percentage}
              onChange={(e) => handlePercentageChange(parseInt(e.target.value))}
              className="w-full h-2 bg-muted rounded-lg appearance-none cursor-pointer accent-primary"
            />
            <div className="flex justify-between text-xs text-muted-foreground">
              <span>1%</span>
              <span>25%</span>
              <span>50%</span>
              <span>75%</span>
              <span>100%</span>
            </div>
          </div>

          {/* Volume to Close */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-foreground">
              {t('volumeToClose')}
            </label>
            <Input
              type="number"
              step="0.01"
              min="0.01"
              max={totalVolume}
              value={volumeToClose}
              onChange={(e) => handleVolumeChange(e?.target?.value)}
              placeholder="0.00"
              required
            />
          </div>

          {/* Summary */}
          <div className="bg-blue-50 dark:bg-blue-900/20 p-3 rounded border border-blue-200 dark:border-blue-800">
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">{t('closing')}:</span>
                <span className="font-medium text-foreground">{volumeToClose} {t('lot')}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">{t('remaining')}:</span>
                <span className="font-medium text-foreground">{remainingVolume} {t('lot')}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">{t('estimatedProfit')}:</span>
                <span className={`font-medium ${position.profit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {position.profit >= 0 ? '+' : ''}${((position.profit * percentage) / 100).toFixed(2)}
                </span>
              </div>
            </div>
          </div>

          {/* Quick Select Buttons */}
          <div className="grid grid-cols-4 gap-2">
            {[25, 50, 75, 100].map((pct) => (
              <button
                key={pct}
                onClick={() => handlePercentageChange(pct)}
                className={`py-2 px-3 text-sm font-medium rounded transition-colors ${
                  percentage === pct
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-muted text-muted-foreground hover:bg-muted/80'
                }`}
              >
                {pct}%
              </button>
            ))}
          </div>

          {/* Action Buttons */}
          <div className="flex space-x-3 pt-2">
            <Button
              variant="outline"
              onClick={onClose}
              className="flex-1"
            >
              {t('cancel')}
            </Button>
            <Button
              onClick={handlePartialClose}
              className="flex-1 bg-red-600 hover:bg-red-700 text-white"
            >
              {t('closePosition')}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
});

PartialCloseModal.displayName = 'PartialCloseModal';

export default PartialCloseModal;

